<template>
    <div>
        <div class="gradient-box gradient-box-peach" />
        <div class="row" data-test-view="feedback-list">
            <div class="col-md-2 hidden-xs hidden-sm">
                <l-side-menu>
                    <l-side-menu-picture :src="icon"></l-side-menu-picture>

                    <l-side-menu-element
                        v-for="item in menu"
                        :key="item.path"
                        :show="item.show"
                        :type="item.type"
                        :icon="item.icon"
                        :label="item.label"
                        :path="item.path"
                        :handler="() => goTo(item.path)"
                        :active="item.active"
                    ></l-side-menu-element>
                </l-side-menu>
            </div>
            <div class="col-md-10">
                <l-card-content>
                    <l-headline
                        :title="
                            teamSettings &&
                            teamSettings.customWording &&
                            teamSettings.customWording.feedback
                                ? teamSettings.customWording.feedback
                                : $t(`userlist.show.feedbackTitle`)
                        "
                        :tabs="tabs"
                    />

                    <!-- List Views -->
                    <div
                        v-if="['all', 'my'].includes(view)"
                        :data-test-view="view === 'all' ? 'feedback-list-all' : 'feedback-list-you'"
                    >
                        <!-- Feedback Requests -->
                        <l-feedback-list-requests
                            :requests-to-user="requestsToUser"
                            :requests-from-user="requestsFromUser"
                            @click="createFeedbackModal"
                            @dismiss="dismissRequest"
                        />
                        <!--/Feedback Requests -->

                        <!-- Feedback List -->
                        <l-no-data
                            v-show="!list.length"
                            :icon="iconNoData"
                            :text="$t(`userlist.show.listPraiseEmpty`)"
                            data-test-id="feedback-list-no-data"
                        />

                        <div ref="list" class="tw-space-y-4 tw-mt-4">
                            <div
                                class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 xl:tw-grid-cols-3 tw-gap-8"
                            >
                                <div
                                    v-for="feedback in list"
                                    :key="feedback._id"
                                    class="mouseover-parent clickable feedback-list-item"
                                    tabindex="0"
                                    @click="showFeedbackModal({ id: feedback._id })"
                                    @keyup.enter="showFeedbackModal({ id: feedback._id })"
                                >
                                    <l-feedback-list-item :feedback="feedback" />
                                </div>
                            </div>
                        </div>

                        <!--/Feedback List -->
                    </div>
                    <!--/List Views -->

                    <!-- Analytics -->
                    <div v-if="view === 'stats'" data-test-view="feedback-list-stats">
                        <l-feedback-analytics />
                    </div>
                    <!-- Analytics -->
                </l-card-content>
            </div>

            <!-- MODAL -->
            <l-modal v-show="showModal" size="m" :close-on-backdrop-click="false" @close="goBack">
                <template #header>
                    <i
                        v-if="showPrivateNoteLock"
                        class="fal fa-lock r-default-margin-s"
                        data-test-id="lock-private-note"
                    ></i>
                    {{ modalTitle }}
                </template>
                <template #body>
                    <router-view @close="goBack" @title="updateModalTitle"></router-view>
                </template>
            </l-modal>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import LCardContent from "@/components/LCardContent";
import LFeedbackListItem from "@/components/LFeedbackListItem";
import LFeedbackListRequests from "@/components/LFeedbackListRequests";
import LHeadline from "@/components/LHeadline";
import LModal from "@/components/LModal";
import LNoData from "@/components/LNoData";
import LSideMenu from "@/components/LSideMenu";
import LSideMenuPicture from "@/components/LSideMenuPicture";
import LSideMenuElement from "@/components/LSideMenuElement";
import LFeedbackAnalytics from "@/components/LFeedbackAnalytics/LFeedbackAnalytics";

import icon from "@/img/icons/feedback.png";
import iconNoData from "@/img/icons/news.png";
import teamPicture from "@/img/icons/team-icon.png";

export default {
    components: {
        LCardContent,
        LFeedbackListItem,
        LFeedbackListRequests,
        LHeadline,
        LModal,
        LNoData,
        LSideMenu,
        LSideMenuPicture,
        LSideMenuElement,
        LFeedbackAnalytics,
    },
    props: {
        view: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            showModal: false,
            modalTitle: "",
            showPrivateNoteLock: false,
        };
    },
    computed: {
        ...mapGetters({
            user: "users/current/user",
            rights: "users/current/rights",
            teamRole: "users/current/teamRole",
            feedbackList: "feedback/feedback/list",
            getRequests: "feedback/request/list",
            getDetails: "feedback/feedback/details",
            teamSettings: "users/current/teamSettings",
        }),
        hasDirectReports() {
            return this.user?.teamRole?.hasDirectReports;
        },
        canAccessAnalytics() {
            return (
                (this.rights.F_INSTANT || this.rights.F_PRAISE) &&
                (this.rights.F_ANALYTICS_COMPANY ||
                    this.rights.F_ANALYTICS_LOCAL_TEAMS ||
                    this.rights.A_GROUP_ACCESS ||
                    this.hasDirectReports)
            );
        },
        list() {
            if (!Array.isArray(this.feedbackList)) return [];

            return this.feedbackList.map(feedback => ({
                ...feedback,
                thanksNoteReceivers: feedback.thanksNoteReceivers?.map(receiver => ({
                    ...receiver,
                    picture:
                        (!receiver.picture || receiver.picture == "/img/icons/user_small.png") &&
                        receiver.name
                            ? teamPicture
                            : receiver.picture,
                })),
            }));
        },
        requests() {
            return this.getRequests();
        },
        requestsFromUser() {
            return this.requests.filter(request => request.sender.user._id === this.user._id);
        },
        requestsToUser() {
            return this.requests.filter(request => request.receiver.user._id === this.user._id);
        },
        menu() {
            return [
                {
                    label: this.$t("userlist.show.btnShowListAllFeedback"),
                    path: "/feedback/list/all",
                    show: true,
                    active: this.view === "all",
                    testId: "feedback-tab-all",
                    icon: "fal fa-users",
                },
                {
                    label: this.$t("userlist.show.btnShowListMyFeedback"),
                    path: "/feedback/list/my",
                    show: true,
                    active: this.view === "my",
                    testId: "feedback-tab-you",
                    icon: "fal fa-user",
                },
                {
                    label: this.$t("userlist.show.btnShowStats"),
                    path: "/feedback/list/stats",
                    show: this.canAccessAnalytics,
                    active: this.view === "stats",
                    testId: "feedback-tab-stats",
                    icon: "fal fa-chart-bar",
                },
            ];
        },
        tabs() {
            return [
                {
                    label: this.$t("userlist.show.btnPrivateNote"),
                    path: `/feedback/list/${this.view}/create?type=privateNote`,
                    show: this.rights.F_INSTANT,
                    testId: "create-private-note",
                },
                {
                    tabClass: "space",
                    show: true,
                },
                {
                    label: this.$t("userlist.show.btnRequestFeedback"),
                    path: `/feedback/list/${this.view}/request`,
                    show: this.rights.F_INSTANT,
                    tabClass: "tw-ml-2",
                    isButton: true,
                },
                {
                    label: this.$t("userlist.show.btnGiveFeedback"),
                    path: `/feedback/list/${this.view}/create`,
                    show: this.rights.F_INSTANT,
                    testId: "give-feedback",
                    tabClass: "tw-ml-2",
                    isButton: true,
                },
                {
                    label: this.$t("userlist.show.btnCreatePraise"),
                    path: `/feedback/list/${this.view}/create/praise`,
                    show: this.rights.F_PRAISE,
                    testId: "send-praise",
                    tabClass: "btn-primary tw-ml-2",
                    isButton: true,
                },
            ];
        },
    },
    watch: {
        $route: {
            immediate: true,
            handler(newRoute) {
                this.showModal = newRoute.meta && newRoute.meta.modal;
                this.modalTitle = this.getModalTitleFromRoute(newRoute);
            },
        },
    },
    created() {
        this.icon = icon;
        this.iconNoData = iconNoData;
    },
    methods: {
        getModalTitleFromRoute(route) {
            if (route.name === "FeedbackDetails") {
                const feedback = this.getDetails(route.params.id);

                return feedback?.title ?? "Feedback";
            }

            if (route.name === "FeedbackRequest") {
                return this.$t(`feedback.request.title`);
            }

            if (route.name === "FeedbackCreate") {
                if (this.$route.query.type === "privateNote") {
                    return this.$t(`feedback.show.titlePrivateNote`);
                }

                return this.$t(`feedback.create.titleGiveFeedback`);
            }

            if (route.name === "FeedbackCreatePraise") {
                return this.$t(`feedback.create.titlePraise`);
            }
        },
        updateModalTitle(title, showPrivateNoteLock) {
            this.modalTitle = title;
            this.showPrivateNoteLock = showPrivateNoteLock;
        },
        createFeedbackModal({ receiver, type }) {
            this.$router.push(
                `/feedback/list/${this.view}/create/?requestedBy=${receiver}&type=${type}`,
            );
        },
        showFeedbackModal({ id }) {
            this.$router.push(`/feedback/list/${this.view}/details/${id}`);
        },

        dismissRequest({ id }) {
            this.$store.dispatch({ type: "feedback/request/dismiss", payload: { id } });
        },

        goBack() {
            this.$router.push(`/feedback/list/${this.view}`);
        },
        goTo(url) {
            this.$router.push(url);
        },
    },
};
</script>

<style scoped>
.feedback-list-item {
    margin-bottom: 10px;
}
</style>
